<template>
  <div :key="id">
    <div v-if="isSearchable" class="mt-3 d-flex flex-row justify-content-between">
      <RsFormInput
        v-model="searchText"
        icon="search"
        :debounce="300"
        type="search"
        :placeholder="placeholder"
      />
      <div class="d-flex">
        <RsButton
          variant="secondary"
          hollow
          @click="handleSelectAll(true)"
        >
          Select All
        </RsButton>
        <RsButton
          variant="secondary"
          class="rs-modal-btn-secondary-margin"
          hollow
          @click="handleSelectAll(false)"
        >
          Deselect All
        </RsButton>
      </div>
    </div>
    <div class="row">
      <div class="col-5" />
      <div class="col-3 text-center">
        <span class="label">No Change</span>
      </div>
      <div class="col-2 text-center">
        <span class="label">On</span>
      </div>
      <div class="col-2 text-center">
        <span class="label">Off</span>
      </div>
    </div>

    <div v-for="([key, option]) in Object.entries(optionsState)" :key="`${key}`">
      <div class="row">
        <div class="col-5">
          <span class="rs-header-text">{{ option.text }}</span>
        </div>
        <div class="col-3 text-center rs-radio">
          <input
            :id="`${id}-${key}-same`"
            :disabled="option.disabled"
            :name="option.text"
            type="radio"
            value="same"
            :checked="optionsState[key].value === 'same'"
            @click="handleStateChange($event, `${option.id}`)"
          >
        </div>
        <div class="col-2 text-center rs-radio">
          <input
            :id="`${id}-${key}-on`"
            :disabled="option.disabled"
            :name="option.text"
            type="radio"
            value="on"
            :style="`margin: 0`"
            :checked="optionsState[key].value === 'on'"
            @click="handleStateChange($event, `${option.id}`)"
          >
        </div>
        <div class="col-2 text-center rs-radio">
          <input
            :id="`${id}-${key}-off`"
            :disabled="option.disabled"
            :name="option.text"
            type="radio"
            value="off"
            :checked="optionsState[key].value === 'off'"
            @click="handleStateChange($event, `${option.id}`)"
          >
        </div>
      </div>
    </div>
  </div>
</template>
<script lang="ts">
import { defineComponent, PropType } from 'vue';
import { ISelectOption } from '@/shared';

export default defineComponent({
  name: 'RsThreeStateRadio',

  props: {
    /**
     * Unique identifier for the component. This is used to have a unique key for
     * this component in the DOM.
     */
    id: {
      type: String,
      default: '',
    },
    /**
     * List of selectable options for the radio buttons.
     */
    selectOptions: {
      type: Array as PropType<ISelectOption[]>,
      default: () => [],
    },
    /**
     * Determines if the search input is displayed.
     */
    isSearchable: {
      type: Boolean,
      default: true,
    },
    /**
     * Placeholder text for the search input.
     */
    placeholder: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      optionsState: {} as Record<string, ISelectOption>,
      searchText: '' as string,
    };
  },
  watch: {
    searchText() {
      this.optionsState = this.buildOptionsState(
        this.selectOptions.filter((option) => option.text.toLowerCase().includes(this.searchText.toLowerCase())),
      );
    },
    selectOptions: {
      immediate: true,
      handler() {
        this.optionsState = this.buildOptionsState(this.selectOptions);
      },
    },
  },
  methods: {
    /**
     * Builds a key-value object from a list of select options.
     * Ensures every option has a default `value` of `'same'` if not already set.
     *
     * @param {ISelectOption[]} options - The list of options to process.
     * @returns {Record<string, ISelectOption>} A dictionary of options by ID.
     */
    buildOptionsState(options: ISelectOption[]): Record<string, ISelectOption> {
      return options.reduce((acc, option) => {
        acc[`${option.id}`] = {
          ...option,
          value: option.value ?? null,
        };
        return acc;
      }, {} as Record<string, ISelectOption>);
    },
    /**
     * Handles the state change of a radio button.
     * @param {PointerEvent} data - The event triggered by the radio button click.
     * @param {string} key - The key of the option being updated.
     */
    handleStateChange(data: PointerEvent | MouseEvent, key: string): void {
      const updated = {
        ...this.optionsState,
        [key]: {
          ...this.optionsState[key],
          value: (data?.target as HTMLInputElement)?.value ?? this.optionsState[key].value,
        },
      };
      this.optionsState = updated;
      this.$emit('on-update', Object.values(updated));
    },
    /**
     * Selects or deselects all options.
     * @param {boolean} isAllSelected - Whether to select or deselect all options.
     */
    handleSelectAll(isAllSelected: boolean): void {
      const updated = Object.values(this.optionsState).reduce((acc, option) => {
        acc[`${option.id}`] = {
          ...option,
          value: isAllSelected ? 'on' : 'off',
        };
        return acc;
      }, {} as Record<string, ISelectOption>);
      this.optionsState = updated;
      this.$emit('on-update', Object.values(updated));
    },

  },
});
</script>
<style lang="scss" scoped>
@import "@/styles/_variables.scss";
.rs-header-text {
    font-weight: normal;
    color: $tertiary-muted;
    font-size: 14px;
    font-family: $body-font-family;
}
.rs-radio input[type='radio'] {
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  width: 16px;
  height: 16px;
  border: 2px solid $tertiary-gray;
  border-radius: 50%;
  background-color: #fff;
  position: relative;
  cursor: pointer;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;

  &:checked {
    border-color: $secondary-color;
    &::before {
      content: '';
      position: absolute;
      top: 50%;
      left: 50%;
      width: 8px;
      height: 8px;
      background-color: $secondary-color;
      border-radius: 50%;
      transform: translate(-50%, -50%);
      transition: transform 0.2s ease;
    }
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
    border-color: $storm-grey;
    background-color: $storm-grey;

    &::before {
      background-color: transparent;
    }
  }

  &:disabled:checked::before {
    background-color: $secondary-color;
  }
}

</style>
